import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import { ptBR } from 'date-fns/locale';
import { Container, Box, Button } from '@material-ui/core/';

import { isValid } from 'date-fns/esm';
import { Typography } from '@material-ui/core';
import TableRegister from '../../components/TableRegister';
import MapMarker from '../../components/MapMarker';
import { useStyles } from './style';
import {
  EducationMission,
  EducationResponse,
} from '../../@types/educationTypes';
import { fetchEducationById } from '../../actions/educationActions';
import ShowSnackbar, { SetSnackbarType } from '../../components/SnackBar';


interface ParamsType {
  pathId: string;
}

const RegisterEducation: React.FC = () => {
  const styles = useStyles();

  const { pathId } = useParams<ParamsType>();

  const [active, setActive] = useState<number>(0);
  const [variant, setVariant] = useState<'text' | 'outlined' | 'contained' | undefined>(
    'text',
  );
  const [snackBar, setSnackbar] = useState<SetSnackbarType>({ open: false, message: '' });


  const educationMissionDetails: EducationMission = {
    tipoDaMissao: '',
    descricaoDoTipoDaMissao: '',
    focoDaMissao: '',
    bairro: '',
    _localId: '',
    descricaoDoFocoDaMissao: '',
    endereco1: '',
    endereco2: '',
    numeroReferencia: '',
  };

  const [register, setRegister] = useState<EducationResponse>({
    uuid: '',
    data: {
      educationMission: educationMissionDetails,
      abordagemDetails: [],
      orientationDetails: [],
      materiaisDetails: [],
      atividadesRealizadasDetails: [],
      equipeMissaoDetails: [],
    },
    occurred_from: '',
    occurred_to: '',
    geom: {
      coordinates: [0, 0],
    },
    schema: '',
    location_text: '',
  });

  useEffect(() => {
    async function loadRegister(id: string) {
      try {
        const registers = await fetchEducationById(id);
        setRegister(registers.data);
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Não foi encontrado a Missão de Educação.',
        });
      }
    }
    loadRegister(pathId);
  }, [pathId]);

  const toDate = (date: string): String => {
    if (isValid(new Date(date))) {
      const newDate = format(new Date(date), 'dd MMMM, yyyy, HH:mm:ss', {
        locale: ptBR,
      });
      return newDate;
    }
    return '';
  };

  return (
    <Container className={styles.container}>
      <Box className={styles.box}>
        <Button
          variant={variant}
          className={`${styles.buttons} ${active === 0 ? styles.buttonActive : ''}`}
          onClick={() => {
            setActive(0);
          }}
        >
          Detalhes da Missão
        </Button>
        <Button
          variant={variant}
          className={`${styles.buttons} ${active === 1 ? styles.buttonActive : ''}`}
          onClick={() => {
            setActive(1);
          }}
        >
          Abordagens
        </Button>
        <Button
          variant={variant}
          className={`${styles.buttons} ${active === 2 ? styles.buttonActive : ''}`}
          onClick={() => {
            setActive(2);
          }}
        >
          Orientações
        </Button>
        <Button
          variant={variant}
          className={`${styles.buttons} ${active === 3 ? styles.buttonActive : ''}`}
          onClick={() => {
            setActive(3);
          }}
        >
          Equipe da Missão
        </Button>
      </Box>

      {/* TOP FORM */}
      {active === 0 && (
        <>
          <Container className={styles.root}>
            <TableRegister
              title={['tipo da missão', 'foco da missão']}
              data={[
                {
                  details: [
                    `${register.data.educationMission?.tipoDaMissao}`,
                    `${register.data.educationMission?.focoDaMissao}`,
                  ],
                },
              ]}
            />
            <TableRegister
              title={['descrição do tipo da missão', 'descrição do foco da missão']}
              data={[
                {
                  details: [
                    `${register.data.educationMission?.descricaoDoTipoDaMissao}`,
                    `${register.data.educationMission?.descricaoDoFocoDaMissao}`,
                  ],
                },
              ]}
            />
            <Container className={styles.containerMap}>
              <MapMarker
                point={[register.geom.coordinates[1], register.geom.coordinates[0]]}
                notDragabble
              />
            </Container>
            <TableRegister
              title={['latitude', 'longitude']}
              data={[
                {
                  details: [
                    `${register.geom.coordinates[1]}`,
                    `${register.geom.coordinates[0]}`,
                  ],
                },
              ]}
            />
            <TableRegister
              title={['data e hora da solicitação', 'data e hora do fim']}
              data={[
                {
                  details: [
                    `${toDate(register?.occurred_from)}`,
                    `${toDate(register?.occurred_to)}`,
                  ],
                },
              ]}
            />
          </Container>
        </>
      )}

      {/* ABORDAGENS */}
      {active === 1 && (
        <>
          <Container className={styles.root}>
            <TableRegister title={['tipos de abordagens', 'quantidade']} />
            {register.data.abordagemDetails?.map((streets) => (
              <TableRegister
                key={`${streets?.tipo}${streets?.quantidade}`}
                data={[
                  {
                    details: [streets?.tipo, `${streets?.quantidade}`],
                  },
                ]}
              />
            ))}
          </Container>

          <Container className={styles.small}>
            {register.data?.abordagemDetails?.map((streets) => (
              <>
                <Typography style={{ margin: '2% 0 2% 0', fontWeight: 'bold' }}>
                  {register.data.abordagemDetails?.indexOf(streets) + 1} de{' '}
                  {register.data.abordagemDetails?.length}
                </Typography>
                <TableRegister
                  title={['tipos de abordagens']}
                  key={`${streets?.tipo}${streets?.quantidade}`}
                />
                <TableRegister
                  data={[
                    {
                      details: [streets?.tipo],
                    },
                  ]}
                />

                <TableRegister title={['quantidade']} />
                <TableRegister
                  data={[
                    {
                      details: [`${streets?.quantidade}`],
                    },
                  ]}
                />
              </>
            ))}
          </Container>
        </>
      )}

      {/* ORIENTAÇÕES */}
      {active === 2 && (
        <>
          <Container className={styles.root}>
            <TableRegister title={['tipos de orientações', 'quantidade']} />
            {register.data?.orientationDetails?.map((streets) => (
              <TableRegister
                key={`${streets?.tipo}${streets?.quantidade}`}
                data={[
                  {
                    details: [streets?.tipo, `${streets?.quantidade}`],
                  },
                ]}
              />
            ))}
          </Container>

          <Container className={styles.small}>
            {register.data?.orientationDetails?.map((streets) => (
              <>
                <Typography style={{ margin: '2% 0 2% 0', fontWeight: 'bold' }}>
                  {register.data?.orientationDetails.indexOf(streets) + 1} de{' '}
                  {register.data?.orientationDetails.length}
                </Typography>
                <TableRegister title={['tipo de orientações']} key={`${streets?.tipo}`} />
                <TableRegister
                  data={[
                    {
                      details: [streets?.tipo],
                    },
                  ]}
                />

                <TableRegister title={['quantidade']} />
                <TableRegister
                  data={[
                    {
                      details: [`${streets?.quantidade}`],
                    },
                  ]}
                />
              </>
            ))}
          </Container>
        </>
      )}

      {/* EQUIPE DA MISSÃO */}
      {active === 3 && (
        <>
          <Container className={styles.root}>
            <TableRegister title={['Nome', 'Funcão', 'Cargo']} />
            {register.data?.equipeMissaoDetails.map((atuacoes) => (
              <TableRegister
                key={`${atuacoes?.nome}${atuacoes?.funcao}${atuacoes?.cargo}`}
                data={[
                  {
                    details: [
                      `${atuacoes?.nome}`,
                      `${atuacoes?.funcao}`,
                      `${atuacoes?.cargo}`,
                    ],
                  },
                ]}
              />
            ))}
          </Container>

          <Container className={styles.small}>
            {register.data?.equipeMissaoDetails.map((atuacoes) => (
              <>
                <Typography style={{ margin: '2% 0 2% 0', fontWeight: 'bold' }}>
                  {register.data?.equipeMissaoDetails.indexOf(atuacoes) + 1} de{' '}
                  {register.data?.equipeMissaoDetails.length}
                </Typography>
                <TableRegister title={['nome']} key={`${atuacoes?.nome}`} />
                <TableRegister
                  data={[
                    {
                      details: [atuacoes?.nome],
                    },
                  ]}
                />

                <TableRegister title={['funcao']} key={`${atuacoes?.funcao}`} />
                <TableRegister
                  data={[
                    {
                      details: [`${atuacoes?.funcao}`],
                    },
                  ]}
                />

                <TableRegister title={['cargo']} key={`${atuacoes?.cargo}`} />
                <TableRegister
                  data={[
                    {
                      details: [`${atuacoes?.cargo}`],
                    },
                  ]}
                />
              </>
            ))}
          </Container>
        </>
      )}
      <ShowSnackbar
        open={snackBar.open}
        message={snackBar.message}
        setSnackbar={setSnackbar}
      />
    </Container>
  );
};

export default RegisterEducation;
